import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import { useContext, useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import Fuse from 'fuse.js';
import { getFileList, addFileToQueue, getDistributorList, downloadIngestionFile, cancelIngestionFile, getPreview, confirmManualUpload, dryRunImport, markIngestionFileImported, getRouteDecision, sqlSkipRows, sqlRevalidate, getTransformDistributorList, stagePreview, stagedDryRun, stagedImport, getFileProcessingCost, repriceFileProcessingCost, getAiCreditMeter, triageFileFeedback, applyFileRemap, reprocessFileWithFeedback, reprocessIngestionFile } from '../../Actions';
import BulkUpload from './BulkUpload';
import ResolveCodesDialog, { applySkips } from '../Shared/ResolveCodesDialog';
import { AppContext } from '../../Context/AppContextProvide';
import { useAlert } from '../../Context/AlertContextProvider';
import { formatDttm } from '../../Util/timezone';

const SHOWROOM_PRICING_TRANSFORM = '_SHOWROOM PRICING_';

function formatTokens(n) {
  if (!n) return '—';
  return n.toLocaleString();
}

// Saved import impact (from ingestion_file.import_metrics). Returns the object or null.
function parseMetrics(m) {
  if (!m) return null;
  try { return typeof m === 'string' ? JSON.parse(m) : m; } catch { return null; }
}
const METRIC_LABELS = [
  ['newProducts', 'New products'],
  ['updatedProducts', 'Products updated'],
  ['newSkus', 'New colors / SKUs'],
  ['newPrices', 'New prices'],
  ['updatedPrices', 'Prices updated'],
  ['removedPrices', 'Prices removed'],
];

function formatDuration(ms) {
  if (!ms) return '—';
  if (ms < 1000) return `${ms}ms`;
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

export default function FileProcessing(props) {
  const { user } = useContext(AppContext);
  const { handleAlertOpen, setMessage, setMessageType } = useAlert();
  const isHidden = !!user?.is_hidden;
  const [refreshData, setRefreshData] = useState(true);
  const [distributorList, setDistributorList] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedDistributor, setSelectedDistributor] = useState('');
  const [fuzzyMatches, setFuzzyMatches] = useState([]);
  const [fuzzyDialogOpen, setFuzzyDialogOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  // List controls: default to the rows that need attention so dev/test clutter stays out of the way.
  const [statusFilter, setStatusFilter] = useState('action');
  const [searchText, setSearchText] = useState('');
  // AI cost per file (hidden reveal), fetched on demand. Keyed by ingestion_file uuid.
  const [costByUuid, setCostByUuid] = useState({});
  const [costLoadingUuid, setCostLoadingUuid] = useState(null);
  const [repricingUuid, setRepricingUuid] = useState(null);
  // Customer-facing credit meter (points only).
  const [creditMeter, setCreditMeter] = useState(null);
  // Feedback / correction flow — lives inside the preview dialog so the user can type while viewing
  // the data. Keyed off previewFile.
  const [feedbackText, setFeedbackText] = useState('');
  const [triageResult, setTriageResult] = useState(null);
  const [feedbackBusy, setFeedbackBusy] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [cancelDialogFile, setCancelDialogFile] = useState(null);
  const [reprocessDialogFile, setReprocessDialogFile] = useState(null);
  const [reprocessingUuid, setReprocessingUuid] = useState(null);
  const [importDialogFile, setImportDialogFile] = useState(null);
  // Tracked only to guard the import flow; the row's spinner comes from the IMPORTING status.
  const [, setImportingUuid] = useState(null);
  const [dryRunFile, setDryRunFile] = useState(null);
  const [dryRunResult, setDryRunResult] = useState(null);
  const [dryRunLoading, setDryRunLoading] = useState(false);
  const [resolveOpen, setResolveOpen] = useState(false);
  const [resolveIssues, setResolveIssues] = useState([]);
  const [resolveAction, setResolveAction] = useState(null);
  const [resolveFile, setResolveFile] = useState(null);
  // For legacy mapping files, tracks how to resolve unknown codes: SQL (server-staged, by fileName)
  // vs JSON (in-memory). Null for the AI passthrough path (always JSON).
  const [resolveCtx, setResolveCtx] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  // Mapping (legacy-engine) uploads are synchronous and not tracked in ingestion_file, so they live
  // in client state and are merged into the file list. mappingBusy shows a spinner while the initial
  // transform/preview runs (SQL staging on large ERP files can take a while).
  const [mappingFiles, setMappingFiles] = useState([]);
  const [mappingBusy, setMappingBusy] = useState(false);
  const fuseRef = useRef(null);
  const isDistributorNameValErr = /[^a-zA-Z0-9\s'_-]/g.test(selectedDistributor) && !distributorList?.find(d => d.distributor_name === selectedDistributor);

  useEffect(() => {
    async function getPageData() {
      const fileRes = await getFileList().catch(() => null);
      setFileList(fileRes?.data ?? []);

      getAiCreditMeter()
        .then((r) => setCreditMeter(r.data))
        .catch(() => setCreditMeter(null));

      // Unified vendor list = AI distributors + every active mapping name (real distributors plus
      // ERP exports like RFMS / QFloors that live only in file_upload_transform). Merged by name so
      // mapped vendors are selectable; the server pre-flight decides mapping vs AI per upload.
      const [distributorRes, transformRes] = await Promise.all([
        getDistributorList().catch(() => null),
        // Only SPEC (rule-builder) transforms belong on this page; JSON/SQL live on the legacy import page.
        getTransformDistributorList('SPEC').catch(() => null),
      ]);
      const aiList = distributorRes?.data ?? [];
      const transforms = transformRes?.data?.data ?? [];
      // Name -> transform_type (SPEC / SQL / JSON) so we can chip a choice as a transform vs a plain
      // vendor (which just supplies AI context data). A name can be both; transform wins for the chip
      // since that's what the dispatch routes on.
      const transformTypeByName = {};
      for (const t of transforms) if (t.distributor_name) transformTypeByName[t.distributor_name] = t.transform_type || 'TRANSFORM';

      const byName = new Map();
      for (const d of aiList) if (d?.distributor_name && !byName.has(d.distributor_name)) byName.set(d.distributor_name, { ...d });
      for (const t of transforms) if (t.distributor_name && !byName.has(t.distributor_name)) byName.set(t.distributor_name, { distributor_name: t.distributor_name, distributor_uuid: '' });
      const mergedList = Array.from(byName.values())
        .map((d) => ({ ...d, transformType: transformTypeByName[d.distributor_name] || null }))
        .sort((a, b) => a.distributor_name.localeCompare(b.distributor_name));
      setDistributorList(mergedList);

      fuseRef.current = mergedList.length ? new Fuse(
        mergedList,
        { 
          keys: ['distributor_name'], 
          threshold: 0.2, 
          isCaseSensitive: false, 
          shouldSort: true, 
          ignoreLocation: true, 
          includeScore: true 
        }
      ) : null;
    }

    if (refreshData) {
      getPageData();
      setRefreshData(false);
    }
  }, [refreshData])

  // Auto-refresh while any file is in a transient state (importing / processing / queued) so the
  // background import and processor progress update on their own.
  useEffect(() => {
    const transient = new Set(['importing', 'processing', 'waiting in queue']);
    const anyTransient = (fileList ?? []).some((f) => transient.has((f.status ?? '').toLowerCase()));
    if (!anyTransient) return undefined;
    const t = setInterval(() => setRefreshData(true), 5000);
    return () => clearInterval(t);
  }, [fileList])

  function triggerCsvDownload(blob, filename) {
    const baseName = (filename || 'download').replace(/\.[^/.]+$/, '');
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${baseName}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => window.URL.revokeObjectURL(url), 100);
  }

  // Turn a preview (array-of-arrays: row 0 = header) into CSV text.
  function previewToCsv(dataPreview) {
    return (dataPreview ?? [])
      .map((row) => row
        .map((cell) => {
          const s = cell == null ? '' : String(cell);
          return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        })
        .join(','))
      .join('\n');
  }

  async function handleDownload(file) {
    // Mapping files have no S3 object — build the CSV from their resolved (transformed) preview.
    if (file.mapping) {
      const csv = previewToCsv(file.resolvedPreview?.dataPreview);
      if (!csv) { notify('error', 'Nothing to download.'); return; }
      triggerCsvDownload(new Blob([csv], { type: 'text/csv' }), file.filename);
      return;
    }
    const res = await downloadIngestionFile(file.ingestion_file_uuid).catch(() => null);
    if (!res) return;
    triggerCsvDownload(new Blob([res.data]), file.filename);
  }

  function notify(type, msg) {
    setMessageType(type);
    setMessage(msg);
    handleAlertOpen();
  }

  // Is this ingestion_file a processor SPEC (rule-builder) file? Those use server-side staging for
  // preview/dry-run/import; AI files use the _SHOWROOM PRICING_ passthrough below.
  async function getSpecInfo(file) {
    if (file.mapping) return { isSpec: false };
    const d = await getRouteDecision(file.distributor_name, file.filename)
      .then((r) => r.data)
      .catch(() => null);
    return { isSpec: d?.engine === 'mapping' && d?.transformType === 'SPEC' };
  }

  // Preview payload for a file.
  //  - Mapping files (Phase B, legacy inline JSON/SQL): run the ORIGINAL upload through the vendor transform.
  //  - AI files: run the processed CSV through the _SHOWROOM PRICING_ passthrough transform.
  // (SPEC files are handled separately via server-side staging — see getSpecInfo / stage-* endpoints.)
  async function getProcessedPreview(file) {
    if (file.mapping) {
      const formData = new FormData();
      formData.append('dist', file.transform);
      formData.append('priceList', file.originalFile);
      formData.append('collectIssues', 'true');
      const preview = await getPreview(formData).catch(() => null);
      return preview?.data ?? null;
    }

    const dl = await downloadIngestionFile(file.ingestion_file_uuid).catch(() => null);
    if (!dl) return null;
    const baseName = (file.filename || file.ingestion_file_uuid).replace(/\.[^/.]+$/, '');
    const csvFile = new File([dl.data], `${baseName}.csv`, { type: 'text/csv' });
    const formData = new FormData();
    formData.append('dist', SHOWROOM_PRICING_TRANSFORM);
    formData.append('priceList', csvFile);
    formData.append('collectIssues', 'true');

    const preview = await getPreview(formData).catch(() => null);
    return preview?.data ?? null;
  }

  // Open the resolve-unknown-codes dialog and remember what to resume afterward.
  function openResolve(issues, action, file) {
    setResolveIssues(issues);
    setResolveAction(action);
    setResolveFile(file);
    setResolveOpen(true);
  }

  async function handleDryRun(file, skipSpecs = []) {
    setDryRunFile(file);
    setDryRunResult(null);
    setDryRunLoading(true);

    // SPEC files: counts come from the server-side staged table (all rows), not a client sample.
    if (!file.mapping) {
      const { isSpec } = await getSpecInfo(file);
      if (isSpec) {
        const res = await stagedDryRun(file.ingestion_file_uuid).catch(() => null);
        setDryRunLoading(false);
        if (!res) { setDryRunFile(null); notify('error', 'Dry run failed. Please try again.'); return; }
        setDryRunResult({ ...res.data, skippedRows: 0 });
        return;
      }
    }

    // Mapping files arrive with an already-resolved preview (issues, incl. the SQL staged-table
    // loop, are handled before the dry run so we never re-stage). AI files derive it fresh here.
    let preview;
    if (file.mapping) {
      preview = file.resolvedPreview;
      if (!preview) {
        setDryRunLoading(false);
        setDryRunFile(null);
        notify('error', 'Dry run failed. Please try again.');
        return;
      }
    } else {
      const raw = await getProcessedPreview(file);
      if (!raw) {
        setDryRunLoading(false);
        setDryRunFile(null);
        notify('error', 'Dry run failed. Please try again.');
        return;
      }
      preview = applySkips(raw, skipSpecs);
      if (preview.issues?.length) {
        setDryRunLoading(false);
        setDryRunFile(null);
        openResolve(preview.issues, 'dryrun', file);
        return;
      }
      if (preview.dataPreview.length <= 1) {
        setDryRunLoading(false);
        setDryRunFile(null);
        notify('warning', 'All rows were skipped — nothing to preview.');
        return;
      }
    }

    const res = await dryRunImport(preview).catch(() => null);
    setDryRunLoading(false);
    if (!res) {
      setDryRunFile(null);
      notify('error', 'Dry run failed. Please try again.');
      return;
    }
    setDryRunResult({ ...res.data, skippedRows: preview.skippedCount ?? 0 });
  }

  async function runImport(file, skipSpecs = []) {
    if (!file) return;
    setImportingUuid(file.ingestion_file_uuid);

    // SPEC files: import ALL rows from the server-side staged table under the real vendor name.
    if (!file.mapping) {
      const { isSpec } = await getSpecInfo(file);
      if (isSpec) {
        const res = await stagedImport(file.ingestion_file_uuid).catch((e) => e?.response ?? null);
        setImportingUuid(null);
        if (!res || res.status >= 400) {
          notify('error', res?.data?.message || 'Failed to import file. Please try again.');
          return;
        }
        notify('success', 'Import started — it runs in the background; the status will update when it completes.');
        setRefreshData(true);
        return;
      }
    }

    let preview;
    if (file.mapping) {
      preview = file.resolvedPreview;
      if (!preview) {
        setImportingUuid(null);
        notify('error', 'Failed to import file. Please try again.');
        return;
      }
    } else {
      const raw = await getProcessedPreview(file);
      if (!raw) {
        setImportingUuid(null);
        notify('error', 'Failed to import file. Please try again.');
        return;
      }
      preview = applySkips(raw, skipSpecs);
      if (preview.issues?.length) {
        setImportingUuid(null);
        openResolve(preview.issues, 'import', file);
        return;
      }
      if (preview.dataPreview.length <= 1) {
        setImportingUuid(null);
        notify('warning', 'All rows were skipped — nothing to import.');
        return;
      }
    }

    // Capture the impact BEFORE the import (same counts as the dry run) so AI imports also save
    // metrics. Runs against a throwaway temp table; doesn't affect the real import below.
    let metrics = null;
    if (!file.mapping) {
      metrics = await dryRunImport(preview).then((r) => r.data).catch(() => null);
    }

    const res = await confirmManualUpload(preview).catch(() => null);
    if (!res) {
      setImportingUuid(null);
      notify('error', 'Failed to import file. Please try again.');
      return;
    }

    // AI files track lifecycle in ingestion_file (save status + metrics); legacy inline mapping files
    // do not (no row was created), so keep them visible in the list this session, marked IMPORTED,
    // instead of vanishing. (They also have a manual_file record, shown in the "Manual File Uploads" widget.)
    if (file.mapping) {
      setMappingFiles((prev) => prev.map((f) =>
        f.ingestion_file_uuid === file.ingestion_file_uuid ? { ...f, status: 'IMPORTED' } : f));
    } else {
      await markIngestionFileImported(file.ingestion_file_uuid, metrics).catch(() => null);
    }
    setImportingUuid(null);
    const skipped = preview.skippedCount ?? 0;
    notify('success', skipped
      ? `File imported successfully. ${skipped} row${skipped === 1 ? '' : 's'} skipped.`
      : 'File imported successfully.');
    setRefreshData(true);
  }

  function handleImportConfirm() {
    const file = importDialogFile;
    setImportDialogFile(null);
    runImport(file);
  }

  // The shared dialog has already persisted map/create choices; here we just
  // resume the original action with the rows the user chose to skip.
  async function onResolveApply(skipSpecs) {
    const action = resolveAction;
    const file = resolveFile;
    const ctx = resolveCtx;
    setResolveOpen(false);

    // Legacy mapping resolution (mirrors the manual-upload page): SQL codes are resolved against the
    // server-staged table by fileName; JSON codes are filtered in memory. Once clean, we build the
    // resolved preview and drop into the same dry-run/import UI as everything else.
    if (action === 'mapping') {
      setMappingBusy(true);
      try {
        let preview;
        if (ctx?.transformType === 'SQL') {
          for (const s of skipSpecs) {
            await sqlSkipRows(ctx.fileName, s.fieldName, s.value);
          }
          const res = await sqlRevalidate(ctx.fileName);
          if (res.data.issues?.length) {
            setMappingBusy(false);
            setResolveIssues(res.data.issues);
            setResolveOpen(true);
            return;
          }
          preview = {
            ...res.data,
            distributor: ctx.distributor,
            fileName: ctx.fileName,
            transformType: 'SQL',
            dataPreview: res.data.dataPreview,
          };
        } else {
          const raw = await getProcessedPreview(file);
          const filtered = applySkips(raw, skipSpecs);
          if (filtered.issues?.length) {
            setMappingBusy(false);
            setResolveCtx({ transformType: raw.transformType, fileName: raw.fileName, distributor: raw.distributor });
            setResolveIssues(filtered.issues);
            setResolveOpen(true);
            return;
          }
          preview = filtered;
        }
        addMappingFile(file, preview);
      } catch (err) {
        setMappingBusy(false);
        notify('error', 'Something went wrong resolving codes. Please try again.');
      }
      return;
    }

    if (action === 'import') runImport(file, skipSpecs);
    else handleDryRun(file, skipSpecs);
  }

  // Add a resolved mapping upload to the list so it exposes Download / Preview / Dry Run / Import,
  // the same as an AI-processed file. Status FILE PROCESSED reuses the existing row action logic.
  function addMappingFile(synth, resolvedPreview) {
    setMappingBusy(false);
    const entry = {
      ...synth,
      resolvedPreview,
      status: 'FILE PROCESSED',
      distributor_name: synth.transform,
      // ISO (UTC) so formatDttm renders it correctly in the user's zone, same as
      // server-provided rows. This is a client-only optimistic row.
      created_dttm: new Date().toISOString(),
      last_modified_dttm: new Date().toISOString(),
      isMappingFile: true, // client-only (not tracked in ingestion_file) — discard drops it locally
    };
    setMappingFiles((prev) => [entry, ...prev.filter((f) => f.ingestion_file_uuid !== entry.ingestion_file_uuid)]);
    notify('success', `"${synth.filename}" is ready — preview, dry run, or import it below.`);
  }

  // Legacy mapping upload: run the original file through the vendor's own transform (JSON or SQL),
  // resolve any unknown codes, then surface it in the list with the standard actions.
  async function startMappingFlow(name, transform, transformType) {
    const synth = {
      mapping: true,
      transform,
      transformType,
      originalFile: selectedFile,
      filename: selectedFile?.name || transform,
      ingestion_file_uuid: `mapping:${transform}:${Date.now()}`,
    };
    setFuzzyDialogOpen(false);
    handleDialogClose();
    setMappingBusy(true);

    const raw = await getProcessedPreview(synth).catch(() => null);
    if (!raw) {
      setMappingBusy(false);
      notify('error', `Could not process this file with the "${transform}" mapping. Please try again.`);
      return;
    }
    if (raw.issues?.length) {
      setMappingBusy(false);
      setResolveCtx({ transformType: raw.transformType || transformType, fileName: raw.fileName, distributor: raw.distributor });
      openResolve(raw.issues, 'mapping', synth);
      return;
    }
    addMappingFile(synth, raw);
  }

  async function handlePreview(file) {
    setPreviewFile(file);
    setPreviewData(null);
    setFeedbackText('');
    setTriageResult(null);
    // Mapping files already hold their resolved preview — reuse it (never re-run the transform, which
    // would re-stage the SQL table). AI files fetch it fresh.
    if (file.mapping) {
      const dp = file.resolvedPreview?.dataPreview;
      if (!dp?.length) {
        setPreviewFile(null);
        notify('error', 'Could not load preview. Please try again.');
        return;
      }
      setPreviewData(dp);
      return;
    }
    setPreviewLoading(true);
    // SPEC files: sample comes from the server-side staged table (staged lazily on first open).
    const { isSpec } = await getSpecInfo(file);
    const preview = isSpec
      ? await stagePreview(file.ingestion_file_uuid).then((r) => r.data).catch(() => null)
      : await getProcessedPreview(file);
    setPreviewLoading(false);
    if (!preview?.dataPreview?.length) {
      setPreviewFile(null);
      notify('error', 'Could not load preview. Please try again.');
      return;
    }
    setPreviewData(preview.dataPreview);
  }

  async function handleCancelConfirm() {
    const file = cancelDialogFile;
    setCancelDialogFile(null);
    if (!file) return;

    // Client-only mapping uploads were never persisted — just drop them from the list.
    if (file.isMappingFile) {
      setMappingFiles((prev) => prev.filter((f) => f.ingestion_file_uuid !== file.ingestion_file_uuid));
      setMessageType('success');
      setMessage('File removed.');
      handleAlertOpen();
      return;
    }

    const uuid = file.ingestion_file_uuid;
    if (!uuid) return;
    const res = await cancelIngestionFile(uuid).catch(() => null);
    if (!res) {
      setMessageType('error');
      setMessage('Failed to remove file. Please try again.');
      handleAlertOpen();
      return;
    }
    setMessageType('success');
    setMessage('File removed successfully.');
    handleAlertOpen();
    setRefreshData(true);
  }

  // Reveal a file's real cost + model — HIDDEN users only (the endpoint is hidden-gated; customers
  // only ever see points). Triggered by the "Show cost & model" button.
  async function loadCost(file) {
    const uuid = file?.ingestion_file_uuid;
    if (!uuid || !isHidden || file.isMappingFile || costByUuid[uuid] || costLoadingUuid === uuid) return;
    setCostLoadingUuid(uuid);
    const res = await getFileProcessingCost(uuid).catch(() => null);
    setCostByUuid((prev) => ({ ...prev, [uuid]: res?.data ?? { runs: [], totals: null } }));
    setCostLoadingUuid(null);
  }

  // Re-price a file's stored cost rows against the live AWS price (no re-run of the model).
  async function handleReprice(file) {
    const uuid = file?.ingestion_file_uuid;
    if (!uuid) return;
    setRepricingUuid(uuid);
    const res = await repriceFileProcessingCost(uuid).catch(() => null);
    setRepricingUuid(null);
    if (!res) {
      notify('error', 'Reprice failed. Please try again.');
      return;
    }
    const { repriced = 0, stillUnavailable = 0 } = res.data || {};
    // Refresh the now-updated cost record.
    const fresh = await getFileProcessingCost(uuid).catch(() => null);
    setCostByUuid((prev) => ({ ...prev, [uuid]: fresh?.data ?? prev[uuid] }));
    notify(
      stillUnavailable ? 'warning' : 'success',
      stillUnavailable
        ? `Repriced ${repriced}; ${stillUnavailable} still unavailable (check pricing IAM / model match).`
        : `Repriced ${repriced} cost row(s).`
    );
  }

  // ── Feedback / correction (inside the preview dialog) ──
  function closePreview() {
    setPreviewFile(null);
    setPreviewData(null);
    setFeedbackText('');
    setTriageResult(null);
    setFeedbackBusy(false);
  }

  // A file that's been processed (or whose import failed) can still be dry-run / imported. Drives the
  // Dry Run + Import buttons now surfaced from inside the Preview modal.
  function canImportFile(file) {
    const s = (file?.status ?? '').toLowerCase();
    return s === 'file processed' || s === 'import failed';
  }

  // Only AI-processed files (with a server-side processed.csv) can be corrected. Mapping/client-only
  // files and already-imported files are view-only in the preview.
  function isCorrectable(file) {
    if (!file || file.isMappingFile || file.mapping) return false;
    const s = (file.status ?? '').toLowerCase();
    return s === 'file processed' || s === 'import failed';
  }

  async function handleTriage() {
    const uuid = previewFile?.ingestion_file_uuid;
    if (!uuid || !feedbackText.trim()) return;
    setFeedbackBusy(true);
    const res = await triageFileFeedback(uuid, feedbackText.trim()).catch((e) => e?.response ?? null);
    setFeedbackBusy(false);
    if (!res || res.status >= 400) {
      notify('error', res?.data?.message || 'Could not analyze the feedback. Try rephrasing.');
      return;
    }
    setTriageResult(res.data);
  }

  async function handleApplyRemap() {
    const uuid = previewFile?.ingestion_file_uuid;
    if (!uuid || !triageResult?.transforms) return;
    setFeedbackBusy(true);
    const res = await applyFileRemap(uuid, triageResult.transforms).catch(() => null);
    if (!res) { setFeedbackBusy(false); notify('error', 'Could not apply the correction.'); return; }
    notify('success', 'Output corrected — reloading the preview.');
    setTriageResult(null);
    setFeedbackText('');
    setRefreshData(true);
    // Reload the preview in place so the user sees the corrected data.
    const fresh = await getProcessedPreview(previewFile).catch(() => null);
    setFeedbackBusy(false);
    if (fresh?.dataPreview?.length) setPreviewData(fresh.dataPreview);
  }

  async function handleReprocess() {
    const uuid = previewFile?.ingestion_file_uuid;
    if (!uuid || !feedbackText.trim()) return;
    setFeedbackBusy(true);
    const res = await reprocessFileWithFeedback(uuid, feedbackText.trim()).catch((e) => e?.response ?? null);
    setFeedbackBusy(false);
    if (res?.status === 402) {
      if (res.data?.meter) setCreditMeter(res.data.meter);
      notify('warning', res.data?.message || 'Monthly AI credit limit reached.');
      return;
    }
    if (!res || res.status >= 400) { notify('error', res?.data?.message || 'Could not start reprocessing.'); return; }
    notify('success', 'Reprocessing started — it runs in the background and uses credits.');
    closePreview();
    setRefreshData(true);
  }

  // Plain re-run from the row action — no feedback. The server rebuilds the AI reference context from
  // the current database, so a file processed under older context picks up the newer one.
  async function handleReprocessConfirm() {
    const file = reprocessDialogFile;
    setReprocessDialogFile(null);
    const uuid = file?.ingestion_file_uuid;
    if (!uuid) return;

    setReprocessingUuid(uuid);
    const res = await reprocessIngestionFile(uuid).catch((e) => e?.response ?? null);
    setReprocessingUuid(null);

    if (res?.status === 402) {
      if (res.data?.meter) setCreditMeter(res.data.meter);
      notify('warning', res.data?.message || 'Monthly AI credit limit reached.');
      return;
    }
    if (!res || res.status >= 400) {
      notify('error', res?.data?.message || 'Could not start reprocessing.');
      return;
    }
    notify('success', 'Reprocessing started — it runs in the background and uses credits.');
    setRefreshData(true);
  }

  function handleDialogClose() {
    setDialogOpen(false);
    setSelectedFile(null);
    setSelectedDistributor('');
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple: false,
    // Types the file-processor can ingest: tabular (csv/xls/xlsx) + multimodal (pdf + images).
    // Each extension is listed so the OS picker matches by name even when it reports an
    // unexpected MIME (e.g. macOS tagging .csv as application/vnd.ms-excel), which otherwise
    // greys the file out.
    accept: {
      'application/pdf': ['.pdf'],
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xls', '.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/webp': ['.webp'],
    },
    onDrop: (accepted) => { if (accepted[0]) setSelectedFile(accepted[0]); },
  });

  async function submitWithName(name) {
    setIsUploading(true);

    // Pre-flight: let the server decide the engine.
    //  - JSON/SQL mapping -> legacy inline engine on the server (synchronous startMappingFlow).
    //  - SPEC mapping     -> the standalone processor (async queue, like AI) — the spec is applied
    //                        there; fall through to addFileToQueue (the server does NOT 409 SPEC).
    //  - no mapping (AI)  -> async processor queue.
    // The server also hard-guards JSON/SQL with a 409, handled below as a fallback.
    const decision = await getRouteDecision(name, selectedFile?.name)
      .then((r) => r.data)
      .catch(() => null);

    if (decision?.engine === 'mapping' && decision.transformType !== 'SPEC') {
      setIsUploading(false);
      startMappingFlow(name, decision.transform, decision.transformType);
      return;
    }

    const formData = new FormData();
    formData.append('dataFile', selectedFile);

    const distributorUUID = distributorList?.find(d => d.distributor_name === name)?.distributor_uuid;

    const res = await addFileToQueue(formData, name, distributorUUID ?? '', selectedFile?.name).catch((err) => err?.response ?? null);
    setIsUploading(false);
    // 409 = the server guard caught a mapped vendor the pre-flight missed; run the mapping flow.
    if (res?.status === 409 && res?.data?.engine === 'mapping') {
      startMappingFlow(name, res.data.transform, res.data.transformType);
      return;
    }
    // 402 = monthly AI credit limit reached; the file was not queued.
    if (res?.status === 402) {
      if (res.data?.meter) setCreditMeter(res.data.meter);
      setMessageType('warning');
      setMessage(res.data?.message || 'Monthly AI credit limit reached. New AI files are paused until your credits reset.');
      handleAlertOpen();
      setFuzzyDialogOpen(false);
      handleDialogClose();
      return;
    }
    if (!res || res.status >= 400) {
      setMessageType('error');
      setMessage('Failed to upload file. Please try again.');
      handleAlertOpen();
      setFuzzyDialogOpen(false);
      handleDialogClose();
      return;
    }
    setMessageType('success');
    setMessage('File uploaded successfully.');
    handleAlertOpen();
    setRefreshData(true);
    setFuzzyDialogOpen(false);
    handleDialogClose();
  }

  function handleSubmitClick() {
    const exactMatch = distributorList?.find(d => d.distributor_name === selectedDistributor);
    if (exactMatch) {
      submitWithName(selectedDistributor);
      return;
    }

    const typedLower = selectedDistributor.toLowerCase();
    const strippedOfSpecialChars = typedLower.replace(/[\s'_-]/g, '');
    const closeMatches = [];

    const substringMatches = distributorList?.filter(d => {
      const nameLower = d.distributor_name.toLowerCase();
      return typedLower.includes(nameLower) || nameLower.includes(typedLower) || strippedOfSpecialChars.includes(nameLower) || nameLower.includes(strippedOfSpecialChars);
    })?.map(d => d.distributor_name);

    if (substringMatches?.length){
      closeMatches.push(...substringMatches);
    }

    const fuseMatches = fuseRef.current?.search(selectedDistributor)?.filter(r => r.item.distributor_name !== selectedDistributor)?.map(r => r.item.distributor_name);
    const seen = new Set(substringMatches);
    const deduppedMatches = fuseMatches?.filter(n => !seen?.has(n));

    if (deduppedMatches?.length) {
      closeMatches.push(...deduppedMatches);
    }

    if (closeMatches.length === 0) {
      submitWithName(selectedDistributor);
    } else {
      setFuzzyMatches(closeMatches);
      setFuzzyDialogOpen(true);
    }
  }

  // Mapping (legacy-engine) files are client-only and shown first, above the AI-tracked files.
  const combinedFiles = [...mappingFiles, ...(fileList ?? [])];

  // Bucket each file by how much attention it needs, so actionable rows surface first and the user
  // can filter the list down when a dev instance fills up with files in every state.
  // 'import failed' stays under `action`, not `failed`: extraction succeeded, so the row still renders
  // working Preview / Dry Run / Import buttons and is genuinely actionable. Only 'processing failed'
  // has no output at all.
  const fileGroup = (status) => {
    const s = (status ?? '').toLowerCase();
    if (s === 'file processed' || s === 'import failed') return 'action';
    if (s === 'pending upload' || s === 'waiting in queue' || s === 'processing' || s === 'importing') return 'progress';
    if (s === 'processing failed') return 'failed';
    return 'done'; // imported, cancelled
  };
  const GROUP_ORDER = { action: 0, progress: 1, failed: 2, done: 3 };
  const parseTs = (s) => {
    if (!s) return 0;
    const t = Date.parse(s);
    if (!Number.isNaN(t)) return t;
    const t2 = Date.parse(String(s).replace(' ', 'T'));
    return Number.isNaN(t2) ? 0 : t2;
  };

  const groupCounts = combinedFiles.reduce(
    (acc, f) => { acc[fileGroup(f.status)] += 1; return acc; },
    { action: 0, progress: 0, failed: 0, done: 0 }
  );
  const STATUS_FILTERS = [
    { key: 'action', label: 'Needs action', count: groupCounts.action },
    { key: 'progress', label: 'In progress', count: groupCounts.progress },
    { key: 'failed', label: 'Failed', count: groupCounts.failed },
    { key: 'done', label: 'Done', count: groupCounts.done },
    { key: 'all', label: 'All', count: combinedFiles.length },
  ];

  const search = searchText.trim().toLowerCase();
  const visibleFiles = combinedFiles
    .filter((f) => statusFilter === 'all' || fileGroup(f.status) === statusFilter)
    .filter((f) => !search || (f.filename ?? '').toLowerCase().includes(search))
    .sort((a, b) => {
      const g = GROUP_ORDER[fileGroup(a.status)] - GROUP_ORDER[fileGroup(b.status)];
      if (g !== 0) return g;
      // Oldest activity first within a group, so the longest-waiting file surfaces at the top.
      return parseTs(a.last_modified_dttm) - parseTs(b.last_modified_dttm);
    });
  const pagedFiles = visibleFiles.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const onFilterChange = (key) => { setStatusFilter(key); setPage(0); };

  return (
    <div style={{padding: "30px 20px"}}>
      <Paper style={{padding: "20px"}}>
        <div style={{position: "relative", padding: "15px"}}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap'}}>
                <Typography variant="h4">File List</Typography>
                {creditMeter && creditMeter.allotmentPoints > 0 ? (() => {
                  const pct = Math.min(100, creditMeter.percentUsed || 0);
                  // Quiet by default (neutral blue-grey); only gains color when credits run low.
                  const low = creditMeter.overLimit || pct >= 80;
                  const barColor = creditMeter.overLimit ? '#f44336' : pct >= 80 ? '#b26a00' : '#90a4ae';
                  const reset = creditMeter.resetDate ? new Date(creditMeter.resetDate) : null;
                  const daysLeft = reset ? Math.max(0, Math.ceil((reset.getTime() - Date.now()) / (1000 * 60 * 60 * 24))) : null;
                  return (
                    <div style={{flex: '0 0 auto', width: 240}}>
                      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 3}}>
                        <Typography variant="caption" style={{color: 'rgba(0,0,0,0.45)', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10}}>Credits</Typography>
                        <Typography variant="caption" style={{color: low ? barColor : 'rgba(0,0,0,0.6)', fontWeight: 600}}>
                          {Number(creditMeter.remainingPoints).toLocaleString()} left
                        </Typography>
                      </div>
                      <div style={{height: 5, borderRadius: 3, background: '#eee', overflow: 'hidden'}}>
                        <div style={{width: `${pct}%`, height: '100%', background: barColor, transition: 'width .3s'}} />
                      </div>
                      {/* Only surface the reset once credits are running low (>=80% used). */}
                      {low && (
                        <Typography variant="caption" style={{color: creditMeter.overLimit ? '#f44336' : '#b26a00', display: 'block', marginTop: 2, fontSize: 11, whiteSpace: 'nowrap'}}>
                          {creditMeter.overLimit ? 'limit reached · ' : ''}
                          {daysLeft != null && reset ? `resets in ${daysLeft}d (${reset.toLocaleDateString()})` : ''}
                        </Typography>
                      )}
                    </div>
                  );
                })() : null}
              </div>
            </Grid>
            <Grid item xs={12}>
              <Button variant="contained" onClick={() => setDialogOpen(true)}>Import New File</Button>
              <Button variant="outlined" style={{ marginLeft: 8 }} onClick={() => setBulkOpen(true)}>
                Bulk Upload
              </Button>
            </Grid>
            {combinedFiles.length ? (
              <Grid item xs={12}>
                <div style={{display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap'}}>
                  {STATUS_FILTERS.map((f) => (
                    <Chip
                      key={f.key}
                      label={`${f.label} (${f.count})`}
                      size="small"
                      clickable
                      onClick={() => onFilterChange(f.key)}
                      variant={statusFilter === f.key ? 'default' : 'outlined'}
                      style={statusFilter === f.key
                        ? {backgroundColor: '#142e3e', color: '#fff'}
                        : {}}
                    />
                  ))}
                  <div style={{flex: 1}} />
                  <TextField
                    size="small"
                    variant="outlined"
                    placeholder="Search filename…"
                    value={searchText}
                    onChange={(e) => { setSearchText(e.target.value); setPage(0); }}
                    style={{minWidth: '220px'}}
                  />
                </div>
              </Grid>
            ) : null}
            <Grid item xs={12}>
              {combinedFiles.length ? (
                visibleFiles.length ? (
                <Grid container spacing={1}>
                  {pagedFiles.map((file) => {
                    const status = (file.status ?? '').toLowerCase();
                    const statusColor = status.includes('fail') || status === 'cancelled'
                      ? '#f44336'
                      : status === 'imported'
                      ? '#1565c0'
                      : status === 'importing'
                      ? '#b26a00'
                      : status.includes('processed')
                      ? '#388E3C'
                      : 'inherit';
                    const isImporting = status === 'importing';
                    // "file processed" is ready to import; "import failed" can be retried.
                    const isProcessed = status === 'file processed' || status === 'import failed';
                    const isImported = status === 'imported';
                    // Queued/failed-to-process rows can be cancelled; a reviewed-but-unwanted processed
                    // file can be discarded (same action, friendlier label).
                    const isCancellable = status === 'waiting in queue' || status === 'processing failed';
                    const isDiscardable = isProcessed;
                    const hasError = !!file.last_run_error;
                    // Anything that stopped short of being imported can be re-run against the current AI
                    // context. Mapping files are client-only (no ingestion_file row) so they can't be.
                    const isReprocessable =
                      !file.isMappingFile && (isProcessed || status === 'processing failed');

                    return (
                      <Grid item xs={12} key={file.ingestion_file_uuid}>
                        <Accordion variant="outlined" style={{backgroundColor: '#f5f5f5'}}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <div style={{display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', flex: 1, marginRight: '8px'}}>
                              <div style={{flex: '1 1 200px', minWidth: 0}}>
                                <Typography variant="body2" style={{fontWeight: 'bold', wordBreak: 'break-all'}}>
                                  {file.filename}
                                </Typography>
                                <Typography variant="caption" style={{color: 'rgba(0,0,0,0.5)', wordBreak: 'break-all'}}>
                                  {[file.distributor_name, formatDttm(file.last_modified_dttm, { fallback: '' })].filter(Boolean).join(' · ')}
                                </Typography>
                              </div>
                              <Typography variant="caption" style={{color: statusColor, fontWeight: 'bold', whiteSpace: 'nowrap'}}>{file.status}</Typography>
                              {isImporting && <CircularProgress size={14} style={{color: '#b26a00'}} />}
                              {/* Collapsed row keeps only Preview + Discard. Download/Reprocess live in the
                                  expanded view; Dry Run/Import are reached through the Preview modal. */}
                              {(isProcessed || isImported) && (
                                <Button
                                  size="small"
                                  variant="contained"
                                  style={{whiteSpace: 'nowrap', backgroundColor: '#fff'}}
                                  onClick={(e) => { e.stopPropagation(); handlePreview(file); }}
                                >
                                  Preview
                                </Button>
                              )}
                              {isCancellable && (
                                <Button
                                  size="small"
                                  variant="contained"
                                  style={{whiteSpace: 'nowrap', backgroundColor: '#f44336', color: '#fff'}}
                                  onClick={(e) => { e.stopPropagation(); setCancelDialogFile(file); }}
                                >
                                  Cancel
                                </Button>
                              )}
                              {isDiscardable && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  style={{whiteSpace: 'nowrap', borderColor: '#f44336', color: '#f44336'}}
                                  onClick={(e) => { e.stopPropagation(); setCancelDialogFile(file); }}
                                >
                                  Discard
                                </Button>
                              )}
                            </div>
                          </AccordionSummary>
                          <AccordionDetails style={{flexDirection: 'column', gap: '8px', backgroundColor: '#fff', borderRadius: '0 0 4px 4px', overflow: 'hidden'}}>
                            {/* Secondary actions, moved off the collapsed row. */}
                            {(isProcessed || isImported || isReprocessable) && (
                              <div style={{display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 4}}>
                                {(isProcessed || isImported) && (
                                  <Button
                                    size="small"
                                    variant="contained"
                                    style={{whiteSpace: 'nowrap', backgroundColor: '#fff'}}
                                    onClick={(e) => { e.stopPropagation(); handleDownload(file); }}
                                  >
                                    Download
                                  </Button>
                                )}
                                {isReprocessable && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={reprocessingUuid === file.ingestion_file_uuid}
                                    style={{whiteSpace: 'nowrap', borderColor: '#b26a00', color: '#b26a00'}}
                                    onClick={(e) => { e.stopPropagation(); setReprocessDialogFile(file); }}
                                  >
                                    {reprocessingUuid === file.ingestion_file_uuid
                                      ? <CircularProgress size={16} style={{color: '#b26a00'}} />
                                      : 'Reprocess'}
                                  </Button>
                                )}
                              </div>
                            )}
                            <Typography variant="caption">
                              <span style={{color: 'rgba(0,0,0,0.54)'}}>Vendor: </span>
                              {file.distributor_name}
                            </Typography>
                            <Typography variant="caption">
                              <span style={{color: 'rgba(0,0,0,0.54)'}}>Created: </span>
                              {formatDttm(file.created_dttm)}
                            </Typography>
                            <Typography variant="caption">
                              <span style={{color: 'rgba(0,0,0,0.54)'}}>Last Modified: </span>
                              {formatDttm(file.last_modified_dttm)}
                            </Typography>
                            {(() => {
                              const m = parseMetrics(file.import_metrics);
                              if (!m) return null;
                              return (
                                <div style={{marginTop: 4}}>
                                  <Typography variant="caption" style={{color: 'rgba(0,0,0,0.54)', display: 'block', fontWeight: 'bold'}}>Import impact</Typography>
                                  {METRIC_LABELS.filter(([k]) => m[k] != null).map(([k, label]) => (
                                    <Typography key={k} variant="caption" style={{display: 'block'}}>
                                      <span style={{color: 'rgba(0,0,0,0.54)'}}>{label}: </span>
                                      <span style={{fontWeight: 'bold', color: k === 'removedPrices' && m[k] > 0 ? '#f44336' : 'inherit'}}>{Number(m[k]).toLocaleString()}</span>
                                    </Typography>
                                  ))}
                                </div>
                              );
                            })()}
                            {isHidden && (
                              <Typography variant="caption">
                                <span style={{color: 'rgba(0,0,0,0.54)'}}>Processing Time (Latest / Total): </span>
                                {formatDuration(file.latest_processing_time)} / {formatDuration(file.total_processing_time)}
                              </Typography>
                            )}
                            {isHidden && hasError && (
                              <Typography variant="caption" style={{color: '#f44336', wordBreak: 'break-word'}}>
                                <span style={{color: 'rgba(0,0,0,0.54)'}}>Error: </span>
                                {file.last_run_error}
                              </Typography>
                            )}
                            {/* Customer-facing: points only (never cost/model/tokens). */}
                            {!file.isMappingFile && file.points != null && Number(file.points) > 0 && (
                              <Typography variant="caption">
                                <span style={{color: 'rgba(0,0,0,0.54)'}}>Credits used: </span>
                                <span style={{fontWeight: 'bold'}}>{Number(file.points).toLocaleString()} pts</span>
                              </Typography>
                            )}
                            {/* Hidden-only reveal: real cost, model, tokens — behind a button so it's never on-screen by default. */}
                            {isHidden && !file.isMappingFile && (() => {
                              const uuid = file.ingestion_file_uuid;
                              const cost = costByUuid[uuid];
                              if (!cost) {
                                return (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={costLoadingUuid === uuid}
                                    onClick={(e) => { e.stopPropagation(); loadCost(file); }}
                                    style={{whiteSpace: 'nowrap', alignSelf: 'flex-start', marginTop: 4}}
                                  >
                                    {costLoadingUuid === uuid ? <CircularProgress size={14} /> : 'Show cost & model'}
                                  </Button>
                                );
                              }
                              if (!cost.runs || !cost.runs.length) {
                                return <Typography variant="caption" style={{color: 'rgba(0,0,0,0.54)'}}>No cost recorded for this file.</Typography>;
                              }
                              const t = cost.totals || {};
                              const model = (cost.runs.find((r) => r.model) || {}).model;
                              // 'published-fallback' rows carry a real (published) rate — treat as priced.
                              const pending = cost.runs.filter((r) => r.price_source !== 'aws-price-list' && r.price_source !== 'published-fallback');
                              const awaitingSplit = pending.some((r) => r.price_source === 'awaiting-split');
                              return (
                                <div style={{marginTop: 4, paddingTop: 4, borderTop: '1px dashed rgba(0,0,0,0.12)'}}>
                                  <Typography variant="caption" style={{color: 'rgba(0,0,0,0.54)', display: 'block', fontWeight: 'bold'}}>Internal — cost &amp; model</Typography>
                                  <Typography variant="caption" style={{display: 'block'}}>
                                    <span style={{color: 'rgba(0,0,0,0.54)'}}>Cost: </span>
                                    <span style={{fontWeight: 'bold'}}>${(Number(t.costUsd) || 0).toFixed(4)}</span>
                                    <span style={{color: 'rgba(0,0,0,0.54)'}}> = {Number(t.points || 0).toLocaleString()} pts</span>
                                    {cost.runs.length > 1 && <span style={{color: 'rgba(0,0,0,0.54)'}}> ({cost.runs.length} runs)</span>}
                                  </Typography>
                                  {model && (
                                    <Typography variant="caption" style={{display: 'block', wordBreak: 'break-all'}}>
                                      <span style={{color: 'rgba(0,0,0,0.54)'}}>Model: </span>{model}
                                    </Typography>
                                  )}
                                  <Typography variant="caption" style={{display: 'block'}}>
                                    <span style={{color: 'rgba(0,0,0,0.54)'}}>Tokens (in / out): </span>
                                    {Number(t.inputTokens || 0).toLocaleString()} / {Number(t.outputTokens || 0).toLocaleString()}
                                  </Typography>
                                  {pending.length > 0 && (
                                    <div style={{marginTop: 4, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap'}}>
                                      <Typography variant="caption" style={{color: '#b26a00'}}>
                                        {awaitingSplit
                                          ? 'Not priceable — no token split (re-run to price).'
                                          : `${pending.length} run(s) not yet priced.`}
                                      </Typography>
                                      {!awaitingSplit && (
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          disabled={repricingUuid === uuid}
                                          onClick={(e) => { e.stopPropagation(); handleReprice(file); }}
                                          style={{whiteSpace: 'nowrap'}}
                                        >
                                          {repricingUuid === uuid ? <CircularProgress size={14} /> : 'Reprice'}
                                        </Button>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </AccordionDetails>
                        </Accordion>
                      </Grid>
                    );
                  })}
                  <Grid item xs={12}>
                    <TablePagination
                      component="div"
                      count={visibleFiles.length}
                      page={page}
                      rowsPerPage={rowsPerPage}
                      rowsPerPageOptions={[10, 25, 50]}
                      size="small"
                      onPageChange={(e, newPage) => setPage(newPage)}
                      onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    />
                  </Grid>
                </Grid>
                ) : (
                  <Typography style={{color: 'rgba(0,0,0,0.54)'}}>No files match this filter.</Typography>
                )
              ) : (
                <Typography>No files have been found</Typography>
              )}
            </Grid>
          </Grid>
        </div>
      </Paper>

      <BulkUpload
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        distributorList={distributorList}
        onUploaded={() => setRefreshData(true)}
        onNotify={(type, text) => { setMessageType(type); setMessage(text); handleAlertOpen(); }}
      />

      <Dialog open={dialogOpen} onClose={handleDialogClose} fullWidth maxWidth="sm">
        <DialogTitle>Import New File</DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Autocomplete
                freeSolo
                options={distributorList ?? []}
                getOptionLabel={(option) => (typeof option === 'string' ? option : (option.distributor_name || ''))}
                renderOption={(option) => {
                  const tt = option.transformType;
                  const chip = tt === 'SPEC'
                    ? { label: 'Rule mapping', bg: '#e3f2fd', fg: '#1565c0' }
                    : tt === 'SQL'
                    ? { label: 'SQL transform', bg: '#fff3e0', fg: '#b26a00' }
                    : tt === 'JSON'
                    ? { label: 'JSON transform', bg: '#fff3e0', fg: '#b26a00' }
                    : tt
                    ? { label: 'Transform', bg: '#fff3e0', fg: '#b26a00' }
                    : { label: 'Vendor (AI)', bg: '#eeeeee', fg: '#555' };
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 8 }}>
                      <span style={{ wordBreak: 'break-word' }}>{option.distributor_name}</span>
                      <Chip size="small" label={chip.label} style={{ backgroundColor: chip.bg, color: chip.fg, height: 20 }} />
                    </div>
                  );
                }}
                inputValue={selectedDistributor}
                onInputChange={(e, value) => setSelectedDistributor(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Vendor"
                    error={isDistributorNameValErr}
                    variant="outlined"
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <div
                {...getRootProps()}
                style={{
                  border: `2px dashed ${isDragActive ? '#142e3e' : '#aaa'}`,
                  borderRadius: 4,
                  padding: '24px 16px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  background: isDragActive ? '#e3f2fd' : 'transparent',
                }}
              >
                <input {...getInputProps()} />
                <Typography variant="body1" color={isDragActive ? 'primary' : 'textSecondary'}>
                  {selectedFile
                    ? selectedFile.name
                    : isDragActive
                    ? 'Drop file here...'
                    : 'Drag and drop a file here, or click to select. Accepted: PDF, CSV, XLS, XLSX, PNG, JPG, WEBP.'}
                </Typography>
              </div>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button 
            variant="contained" 
            style={{margin: "10px"}}
            onClick={handleDialogClose}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            style={{backgroundColor: "#142e3e", color: "#28c4fc", margin: "10px"}}
            disabled={!selectedFile || !selectedDistributor || isDistributorNameValErr || isUploading}
            onClick={handleSubmitClick}
          >
            {isUploading ? <CircularProgress size={20} style={{color: "#28c4fc"}} /> : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
      {(() => {
        // A processed file is "discarded" (reviewed, not wanted); a queued/failed one is "cancelled".
        const s = (cancelDialogFile?.status ?? '').toLowerCase();
        const isDiscard = s === 'file processed' || s === 'import failed';
        return (
          <Dialog open={!!cancelDialogFile} onClose={() => setCancelDialogFile(null)} fullWidth maxWidth="sm">
            <DialogTitle>{isDiscard ? 'Discard File' : 'Cancel File'}</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Are you sure you want to {isDiscard ? 'discard' : 'cancel'}{' '}
                <strong>{cancelDialogFile?.filename}</strong>?{' '}
                {isDiscard
                  ? 'It has been processed but not imported — the processed data will be removed and it won’t be imported. This cannot be undone.'
                  : 'This cannot be undone.'}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button variant="contained" style={{margin: "10px"}} onClick={() => setCancelDialogFile(null)}>
                Go Back
              </Button>
              <Button variant="contained" style={{backgroundColor: "#f44336", color: "#fff", margin: "10px"}} onClick={handleCancelConfirm}>
                {isDiscard ? 'Discard File' : 'Cancel Processing'}
              </Button>
            </DialogActions>
          </Dialog>
        );
      })()}

      <Dialog open={!!reprocessDialogFile} onClose={() => setReprocessDialogFile(null)} fullWidth maxWidth="sm">
        <DialogTitle>Reprocess File</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Run <strong>{reprocessDialogFile?.filename}</strong> through the file processor again using the
            current AI context. This re-runs the AI and <strong>uses credits</strong>.
          </DialogContentText>
          <DialogContentText variant="body2" style={{color: '#b26a00'}}>
            Any existing processed output for this file is replaced, and a previous preview or dry run is
            discarded. Nothing already imported into your catalog is affected.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" style={{margin: "10px"}} onClick={() => setReprocessDialogFile(null)}>
            Go Back
          </Button>
          <Button
            variant="contained"
            style={{backgroundColor: "#b26a00", color: "#fff", margin: "10px"}}
            onClick={handleReprocessConfirm}
          >
            Reprocess (uses credits)
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={fuzzyDialogOpen} onClose={() => setFuzzyDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Similar Distributor Found</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Similar distributors already exist. Would you like to use one instead, or continue with what you typed?
          </DialogContentText>
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={12}>
              <Typography variant="h6" style={{margin: "10px auto 5px", borderBottom: "1px solid #000000"}}>Similar Options</Typography>
            </Grid>
            {fuzzyMatches.map(name => (
              <Grid item xs={12} md={6} key={`btn-${name}`} style={{textAlign: "right"}}>
                <Button variant="outlined" fullWidth size="small" onClick={() => submitWithName(name)}>Use "{name}"</Button>
              </Grid>
            ))}
            <Grid item xs={12}>
              <Typography variant="h6" style={{margin: "20px auto 5px", borderBottom: "1px solid #000000"}}>What You Typed</Typography>
            </Grid>
            <Grid item xs={12} md={6} style={{textAlign: "right"}}>
              <Button variant="outlined" fullWidth size="small" onClick={() => submitWithName(selectedDistributor)}>Use "{selectedDistributor}"</Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" style={{margin: "15px"}} onClick={() => setFuzzyDialogOpen(false)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={!!importDialogFile} onClose={() => setImportDialogFile(null)} fullWidth maxWidth="sm">
        <DialogTitle>Import Pricing</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Import <strong>{importDialogFile?.filename}</strong> into the catalog using the Showroom Pricing template?
          </DialogContentText>
          <DialogContentText style={{color: '#b26a00'}}>
            This updates live pricing. For every product/color in the file, all existing prices for that item and
            vendor are replaced — any price not present in the file will be removed. Run a Dry Run first to preview the impact.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" style={{margin: "10px"}} onClick={() => setImportDialogFile(null)}>
            Go Back
          </Button>
          <Button variant="contained" style={{backgroundColor: "#142e3e", color: "#28c4fc", margin: "10px"}} onClick={handleImportConfirm}>
            Import
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={!!dryRunFile} onClose={() => { setDryRunFile(null); setDryRunResult(null); }} fullWidth maxWidth="sm">
        <DialogTitle>Dry Run — {dryRunFile?.filename}</DialogTitle>
        <DialogContent>
          {dryRunLoading || !dryRunResult ? (
            <div style={{display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 0'}}>
              <CircularProgress size={20} />
              <Typography variant="body2">Analyzing file against existing data…</Typography>
            </div>
          ) : (
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">
                  {dryRunResult.totalRows} row{dryRunResult.totalRows === 1 ? '' : 's'} in file
                  {dryRunResult.skippedRows > 0 ? ` (${dryRunResult.skippedRows} skipped)` : ''}
                </Typography>
              </Grid>
              {[
                ['New products', dryRunResult.newProducts],
                ['Existing products updated', dryRunResult.updatedProducts],
                ['New colors / SKUs', dryRunResult.newSkus],
                ['New prices', dryRunResult.newPrices],
                ['Prices updated', dryRunResult.updatedPrices],
                ['Prices removed', dryRunResult.removedPrices],
              ].map(([label, value]) => (
                <Grid item xs={12} key={label} style={{display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #eee', padding: '4px 0'}}>
                  <Typography variant="body2">{label}</Typography>
                  <Typography variant="body2" style={{fontWeight: 'bold', color: label === 'Prices removed' && value > 0 ? '#f44336' : 'inherit'}}>{value}</Typography>
                </Grid>
              ))}
              {dryRunResult.removedPrices > 0 && (
                <Grid item xs={12}>
                  <Typography variant="caption" style={{color: '#f44336'}}>
                    {dryRunResult.removedPrices} existing price{dryRunResult.removedPrices === 1 ? '' : 's'} would be removed because the item/vendor appears in the file but that price line does not.
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" style={{margin: "10px"}} onClick={() => { setDryRunFile(null); setDryRunResult(null); }}>
            Close
          </Button>
          <Button
            variant="contained"
            disabled={dryRunLoading || !dryRunResult}
            style={{backgroundColor: "#142e3e", color: "#28c4fc", margin: "10px"}}
            onClick={() => { const f = dryRunFile; setDryRunFile(null); setDryRunResult(null); setImportDialogFile(f); }}
          >
            Import
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={mappingBusy} fullWidth maxWidth="xs">
        <DialogContent>
          <div style={{display: 'flex', alignItems: 'center', gap: '14px', padding: '18px 6px'}}>
            <CircularProgress size={24} />
            <Typography variant="body2">Processing file with the vendor mapping…</Typography>
          </div>
        </DialogContent>
      </Dialog>
      <ResolveCodesDialog
        open={resolveOpen}
        issues={resolveIssues}
        onCancel={() => { setResolveOpen(false); setMappingBusy(false); }}
        onApply={onResolveApply}
        notify={notify}
      />
      <Dialog open={!!previewFile} onClose={closePreview} fullWidth maxWidth="lg">
        <DialogTitle>Preview — {previewFile?.filename}</DialogTitle>
        <DialogContent>
          {previewLoading || !previewData ? (
            <div style={{display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 0'}}>
              <CircularProgress size={20} />
              <Typography variant="body2">Loading data…</Typography>
            </div>
          ) : (
            <>
              <Typography variant="caption" color="textSecondary">
                {previewData.length - 1} row{previewData.length - 1 === 1 ? '' : 's'}
                {previewData.length - 1 > 100 ? ' (showing first 100)' : ''}
              </Typography>
              <TableContainer style={{maxHeight: isCorrectable(previewFile) ? '42vh' : '60vh', overflow: 'auto', marginTop: '8px'}}>
                <Table size="small" stickyHeader style={{width: 'auto', tableLayout: 'auto'}}>
                  <TableHead>
                    <TableRow>
                      {(previewData[0] ?? []).map((col, i) => (
                        <TableCell key={i} style={{fontWeight: 'bold', whiteSpace: 'nowrap', background: '#fafafa', padding: '6px 12px'}} title={col}>{col}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewData.slice(1, 101).map((row, r) => (
                      <TableRow key={r}>
                        {row.map((cell, c) => (
                          <TableCell key={c} style={{whiteSpace: 'nowrap', padding: '4px 12px'}}>{cell}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Correction panel — stays visible while the table above scrolls. */}
              {isCorrectable(previewFile) && (
                <div style={{marginTop: 12, paddingTop: 12, borderTop: '1px solid #eee'}}>
                  <Typography variant="subtitle2">See a problem? Describe it and we’ll fix it.</Typography>
                  <div style={{display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 8, flexWrap: 'wrap'}}>
                    <TextField
                      multiline
                      minRows={2}
                      variant="outlined"
                      placeholder="e.g. The cost column is actually sqft per carton, and the real cost went into retail price override."
                      value={feedbackText}
                      onChange={(e) => { setFeedbackText(e.target.value); setTriageResult(null); }}
                      style={{flex: '1 1 320px'}}
                    />
                    {!triageResult && (
                      <Button
                        variant="contained"
                        disabled={feedbackBusy || !feedbackText.trim()}
                        style={{whiteSpace: 'nowrap', backgroundColor: feedbackBusy || !feedbackText.trim() ? undefined : '#142e3e', color: '#28c4fc'}}
                        onClick={handleTriage}
                      >
                        {feedbackBusy ? <CircularProgress size={18} style={{color: '#28c4fc'}} /> : 'Analyze'}
                      </Button>
                    )}
                  </div>

                  {triageResult?.action === 'remap' && (
                    <div style={{marginTop: 12, padding: 12, background: '#e8f5e9', borderRadius: 4}}>
                      <Typography variant="body2" style={{fontWeight: 'bold', color: '#2e7d32'}}>
                        This can be fixed instantly — free (no reprocessing).
                      </Typography>
                      {triageResult.explanation && (
                        <Typography variant="caption" style={{display: 'block', marginTop: 4}}>{triageResult.explanation}</Typography>
                      )}
                      <Typography variant="caption" style={{display: 'block', marginTop: 8, color: 'rgba(0,0,0,0.6)'}}>
                        Columns changed: {(triageResult.columnsAffected || []).join(', ')}
                      </Typography>
                      {(triageResult.preview || []).length > 0 && (
                        <div style={{marginTop: 8}}>
                          {(triageResult.preview || []).map((p, i) => (
                            <div key={i} style={{marginTop: i ? 6 : 0}}>
                              {(triageResult.columnsAffected || []).map((col) => (
                                <Typography key={col} variant="caption" style={{display: 'block'}}>
                                  <span style={{color: 'rgba(0,0,0,0.5)'}}>{col}: </span>
                                  <span style={{textDecoration: 'line-through', color: '#c62828'}}>{String(p.before?.[col] ?? '') || '∅'}</span>
                                  {' → '}
                                  <span style={{color: '#2e7d32', fontWeight: 'bold'}}>{String(p.after?.[col] ?? '') || '∅'}</span>
                                </Typography>
                              ))}
                            </div>
                          ))}
                        </div>
                      )}
                      <div style={{marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap'}}>
                        <Button variant="contained" disabled={feedbackBusy} style={{backgroundColor: '#2e7d32', color: '#fff'}} onClick={handleApplyRemap}>
                          {feedbackBusy ? <CircularProgress size={18} style={{color: '#fff'}} /> : 'Apply correction (free)'}
                        </Button>
                        <Button variant="outlined" disabled={feedbackBusy} onClick={handleReprocess}>Reprocess instead</Button>
                        <Button variant="text" disabled={feedbackBusy} onClick={() => setTriageResult(null)}>Edit feedback</Button>
                      </div>
                    </div>
                  )}

                  {triageResult?.action === 'reprocess' && (
                    <div style={{marginTop: 12, padding: 12, background: '#fff3e0', borderRadius: 4}}>
                      <Typography variant="body2" style={{fontWeight: 'bold', color: '#b26a00'}}>
                        This needs a full reprocess — it re-runs the AI and uses credits.
                      </Typography>
                      {triageResult.reason && (
                        <Typography variant="caption" style={{display: 'block', marginTop: 4}}>{triageResult.reason}</Typography>
                      )}
                      <div style={{marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap'}}>
                        <Button variant="contained" disabled={feedbackBusy} style={{backgroundColor: '#b26a00', color: '#fff'}} onClick={handleReprocess}>
                          {feedbackBusy ? <CircularProgress size={18} style={{color: '#fff'}} /> : 'Reprocess (uses credits)'}
                        </Button>
                        <Button variant="text" disabled={feedbackBusy} onClick={() => setTriageResult(null)}>Edit feedback</Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" style={{margin: "10px"}} onClick={closePreview}>
            Close
          </Button>
          {/* Carry the flow forward from the preview instead of sending the user back to the row. */}
          {canImportFile(previewFile) && (
            <>
              <Button
                variant="contained"
                style={{margin: "10px", backgroundColor: '#fff'}}
                onClick={() => { const f = previewFile; closePreview(); handleDryRun(f); }}
              >
                Dry Run
              </Button>
              <Button
                variant="contained"
                style={{margin: "10px", backgroundColor: '#142e3e', color: '#28c4fc'}}
                onClick={() => { const f = previewFile; closePreview(); setImportDialogFile(f); }}
              >
                Import
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </div>
  )
}
